import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  IconButton,
  useTheme,
  alpha,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as DoctorIcon,
  CalendarMonth as DateIcon,
  Schedule as TimeIcon,
  Update as UpdateIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { motion } from 'framer-motion';
import type { DoctorShift } from '../../types/doctorShift';
import { extractTimeHm } from '../../utils/shiftTime';

interface DoctorShiftDetailModalProps {
  open: boolean;
  onClose: () => void;
  onEdit?: () => void;
  shift: DoctorShift | null;
}

/**
 * 医師シフト詳細表示モーダルコンポーネント
 * 
 * 意図：
 * - シフト情報の詳細表示
 * - 編集への簡単な導線
 * - 美しい情報レイアウト
 * 
 * 注意点：
 * - 日時フォーマットの統一
 * - レスポンシブ対応
 * 
 * 改善提案：
 * - 関連シフトの表示
 * - 勤務時間統計の表示
 * - カレンダービューへの連携
 */
export const DoctorShiftDetailModal: React.FC<DoctorShiftDetailModalProps> = ({
  open,
  onClose,
  onEdit,
  shift,
}) => {
  const theme = useTheme();

  if (!shift) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.15)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            医師シフト詳細
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Stack spacing={3}>
            {/* 医師ID */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.03),
                borderRadius: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <DoctorIcon color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    医師ID
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                    {shift.doctor_id}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Divider />

            {/* シフト日付 */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <DateIcon color="action" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  シフト日付
                </Typography>
              </Stack>
              <Typography variant="h6" sx={{ ml: 4 }}>
                {format(parseISO(shift.shift_date), 'yyyy年MM月dd日 (EEEE)', { locale: ja })}
              </Typography>
            </Box>

            {/* 勤務時間 */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <TimeIcon color="action" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  勤務時間
                </Typography>
              </Stack>
              
              <Stack direction="row" spacing={2} sx={{ ml: 4 }}>
                <Chip
                  icon={<TimeIcon />}
                  label={`開始: ${extractTimeHm(shift.start_time)}`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<TimeIcon />}
                  label={`終了: ${extractTimeHm(shift.end_time)}`}
                  color="error"
                  variant="outlined"
                />
              </Stack>
            </Box>

            <Divider />

            {/* システム情報 */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <UpdateIcon color="action" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  システム情報
                </Typography>
              </Stack>
              
              <Stack spacing={1} sx={{ ml: 4 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    作成日時
                  </Typography>
                  <Typography variant="body2">
                    {format(parseISO(shift.created_at), 'yyyy/MM/dd HH:mm', { locale: ja })}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    更新日時
                  </Typography>
                  <Typography variant="body2">
                    {format(parseISO(shift.updated_at), 'yyyy/MM/dd HH:mm', { locale: ja })}
                  </Typography>
                </Box>
                
                {shift.upload_batch_id && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      アップロードバッチID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {shift.upload_batch_id}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
        </motion.div>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 3, 
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          gap: 1
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          閉じる
        </Button>
        
        {onEdit && (
          <Button
            onClick={onEdit}
            variant="contained"
            startIcon={<EditIcon />}
            sx={{ borderRadius: 2 }}
          >
            編集
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};